<script setup lang="ts">
import { computed } from 'vue';
import { SearchArticle } from '@/common/api/models.pleased';
import { markSearchTextToHtml } from '@/vendor/pleased-sdk/util';
import { useFieldBorder } from '@/common/customisation/composables';
import {
  createBackgroundStyle,
  createBorderStyle,
  createTextStyle,
  combineStyles,
} from '@/common/customisation/utils/styleUtils';

interface Props {
  item: SearchArticle;
  highlightQuery?: string | null;
  highlightColor?: string | null;
  borderColor?: string | null;
  textColor?: string | null;
  backgroundColor?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  highlightQuery: null,
  highlightColor: null,
  borderColor: null,
  textColor: null,
  backgroundColor: null,
});

const { borderColor: defaultBorderColor } = useFieldBorder();

const computedBorderColor = computed(() => props.borderColor || defaultBorderColor.value);

const itemTitle = computed(() => {
  const title = props.item.title ?? props.item.titleTranslated;

  if (props.highlightQuery) {
    return markSearchTextToHtml(title, props.highlightQuery);
  }

  return title;
});

const itemStyle = computed(() => {
  const highlightColorStyle: Record<string, string> =
    props.highlightColor != null && props.highlightColor !== ''
      ? { '--article-item-highlight-color': props.highlightColor }
      : {};
  return combineStyles(
    createBackgroundStyle(props.backgroundColor ?? undefined),
    createTextStyle(props.textColor ?? undefined),
    createBorderStyle(computedBorderColor),
    highlightColorStyle
  );
});
</script>

<template>
  <li :style="itemStyle" v-on="$listeners">
    <span v-html="itemTitle" />
  </li>
</template>

<style scoped lang="scss">
@import '@/assets/scss/variable.scss';

li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: $article-autocomplete-dropdown-item-color;
  border-width: 1px;
  border-style: solid;
  font-size: 16px;
  margin: 0 16px 16px;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  gap: 48px;
}

:deep(.highlighted-mark) {
  color: var(--article-item-highlight-color, $article-autocomplete-dropdown-item-highlight-color);
  background: transparent;
}
</style>
